import { gql } from "@apollo/client";
import Grid from "atoms/Grid";
import usePagedData from "helpers/usePagedData";
import { ALL_TEAMS } from "molecules/PlayerList";
import Section from "molecules/Section";
import ShowMoreButton from "molecules/ShowMoreButton";
import React from "react";

import ReelCard, { ReelCardFragment } from "./ReelCard";

export const GameOverviewReelsFragment = gql`
  fragment GameOverviewReelsFragment on Game {
    id
    home {
      id
    }
    away {
      id
    }
  }
`;

export const GameOverviewReelsGamePlayerFragment = gql`
  fragment GameOverviewReelsGamePlayerFragment on GamePlayer {
    person {
      id
      fullNameWithAnonymisation
    }
  }
`;

const PLAYER_REELS_QUERY = gql`
  query GameOverviewReels_player($gamePlayerId: ID!, $offset: Int, $limit: Int) {
    gamePlayer(id: $gamePlayerId) {
      id
      playerReelViews(offset: $offset, limit: $limit) {
        id
        ...ReelCardFragment
      }
      playerReelViewsCount
    }
  }
  ${ReelCardFragment}
`;

const PLAYER_TEAM_REELS_QUERY = gql`
  query GameOverviewReels_playerTeam($gamePlayerId: ID!, $offset: Int, $limit: Int) {
    gamePlayer(id: $gamePlayerId) {
      id
      team {
        id
        name
      }
      gameReelViews(offset: $offset, limit: $limit) {
        id
        ...ReelCardFragment
      }
      gameReelViewsCount
    }
  }
  ${ReelCardFragment}
`;

const GAME_REELS_QUERY = gql`
  query GameOverviewReels_game($gameId: ID!, $offset: Int, $limit: Int) {
    game(id: $gameId) {
      id
      reelViews(offset: $offset, limit: $limit) {
        id
        ...ReelCardFragment
      }
      reelViewsCount
    }
  }
  ${ReelCardFragment}
`;

const TEAM_REELS_QUERY = gql`
  query GameOverviewReels_team($gameId: ID!, $teamId: ID!, $offset: Int, $limit: Int) {
    game(id: $gameId) {
      id
      participation(teamId: $teamId) {
        id
        team {
          id
          name
        }
        reelViews(offset: $offset, limit: $limit) {
          id
          ...ReelCardFragment
        }
        reelViewsCount
      }
    }
  }
  ${ReelCardFragment}
`;

// Wide enough for 5 reels per row on the full content width
const REEL_WIDTH = 200;
// Same on every screen width: two rows of 5 on desktop
const REELS_PER_PAGE = 10;

function ReelSection({ title, query, variables, selectPage }) {
  const { page, items, remaining, loading, loadMore } = usePagedData(query, variables, selectPage, {
    pageSize: REELS_PER_PAGE,
  });

  if (!items.length) {
    return null;
  }

  return (
    <Section title={typeof title === "function" ? title(page) : title}>
      <Grid gridItemWidth={`${REEL_WIDTH}px`}>
        {items.map((reelView) => (
          <ReelCard key={reelView.id} reelView={reelView} />
        ))}
      </Grid>
      <ShowMoreButton remaining={remaining} noun="reel" onClick={loadMore} disabled={loading} />
    </Section>
  );
}

// Remount (key) on every team or player change so the loaded pages start over.
export default function GameOverviewReels({ game, gamePlayer, teamId }) {
  if (gamePlayer) {
    const playerName = gamePlayer.person?.fullNameWithAnonymisation;
    return (
      <>
        <ReelSection
          title={playerName ? `${playerName}'s Reels` : "Player Reels"}
          query={PLAYER_REELS_QUERY}
          variables={{ gamePlayerId: gamePlayer.id }}
          selectPage={(data) => ({
            items: data.gamePlayer?.playerReelViews,
            total: data.gamePlayer?.playerReelViewsCount,
          })}
        />
        <ReelSection
          title={(page) => (page?.teamName ? `${page.teamName} Game Reels` : "Team Game Reels")}
          query={PLAYER_TEAM_REELS_QUERY}
          variables={{ gamePlayerId: gamePlayer.id }}
          selectPage={(data) => ({
            items: data.gamePlayer?.gameReelViews,
            total: data.gamePlayer?.gameReelViewsCount,
            teamName: data.gamePlayer?.team?.name,
          })}
        />
      </>
    );
  }

  const teamIds = teamId === ALL_TEAMS ? [game?.home?.id, game?.away?.id].filter(Boolean) : [teamId];

  return (
    <>
      {teamId === ALL_TEAMS && (
        <ReelSection
          title="Game Reels"
          query={GAME_REELS_QUERY}
          variables={{ gameId: game?.id }}
          selectPage={(data) => ({ items: data.game?.reelViews, total: data.game?.reelViewsCount })}
        />
      )}
      {teamIds.map((id) => (
        <ReelSection
          key={id}
          title={(page) => (page?.teamName ? `${page.teamName} Game Reels` : "Team Game Reels")}
          query={TEAM_REELS_QUERY}
          variables={{ gameId: game?.id, teamId: id }}
          selectPage={(data) => ({
            items: data.game?.participation?.reelViews,
            total: data.game?.participation?.reelViewsCount,
            teamName: data.game?.participation?.team.name,
          })}
        />
      ))}
    </>
  );
}
